import React, { useEffect, useState, useCallback, Fragment } from 'react';
import apiClient from '../api/axios';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert, ListGroup } from 'react-bootstrap';
import { formatUtcToLocal } from '../utils/dateUtils';
import { toast } from 'react-toastify';

const AdminOrganizationDetails = () => {
    const { orgId } = useParams();
    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchOrganizationDetails = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get(`/admin/organizations/${orgId}`);
            setOrganization(response.data);
            setLoading(false);
        } catch (err) {
            setError('Не удалось загрузить детали организации. У вас есть права доступа?');
            setLoading(false);
            console.error(err);
        }
    }, [orgId]);

    useEffect(() => {
        fetchOrganizationDetails();
    }, [fetchOrganizationDetails]);

    const bytesToMB = (bytes) => {
        if (typeof bytes !== 'number') return 'N/A';
        return (bytes / (1024 * 1024)).toFixed(2);
    };

    if (loading) {
        return (
            <Container className="mt-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Загрузка...</span>
                </Spinner>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    if (!organization) {
        return (
            <Container className="mt-5">
                <Alert variant="info">Организация не найдена.</Alert>
            </Container>
        );
    }

    return (
        <Container className="mt-5">
            <h1 className="mb-4">{organization.name}</h1>
            <Link to="/admin/organizations" className="btn btn-secondary mb-4">
                <i className="fas fa-arrow-left me-2"></i>К списку организаций
            </Link>

            <Row>
                <Col md={6} className="mb-4">
                    <Card>
                        <Card.Header><i className="fas fa-info-circle me-2"></i>Общая информация</Card.Header>
                        <ListGroup variant="flush">
                            <ListGroup.Item><strong>Описание:</strong> {organization.description || 'Не указано'}</ListGroup.Item>
                            <ListGroup.Item>
                                <strong>Фонотека публична:</strong> {organization.is_songs_library_public ? 
                                <i className="fas fa-check-circle text-success ms-2"></i> : 
                                <i className="fas fa-times-circle text-danger ms-2"></i>}
                            </ListGroup.Item>
                        </ListGroup>
                    </Card>
                    <Card className="mt-4"> {/* Добавляем отступ сверху */}
                        <Card.Header><i className="fas fa-calendar-alt me-2"></i>Последние события</Card.Header>
                        <ListGroup variant="flush">
                            {organization.latest_events && organization.latest_events.length > 0 ? (
                                organization.latest_events.map(event => (
                                    <ListGroup.Item key={event.id}>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <a href={"/events/"+event.id}>{event.name}</a>
                                            <small className="text-muted">{formatUtcToLocal(event.date)}</small>
                                        </div>
                                    </ListGroup.Item>
                                ))
                            ) : (
                                <ListGroup.Item>Нет последних событий.</ListGroup.Item>
                            )}
                        </ListGroup>
                    </Card>
                </Col>
                <Col md={6} className="mb-4">
                    <Card>
                        <Card.Header><i className="fas fa-chart-bar me-2"></i>Статистика</Card.Header>
                        <ListGroup variant="flush">
                            <ListGroup.Item><strong>Событий:</strong> {organization.event_count}</ListGroup.Item>
                            <ListGroup.Item><strong>Пользователей:</strong> {organization.user_count}</ListGroup.Item>
                            <ListGroup.Item><strong>Песен:</strong> {organization.song_count}</ListGroup.Item>
                            <ListGroup.Item><strong>Аранжировок:</strong> {organization.arrangement_count}</ListGroup.Item>
                            <ListGroup.Item>
                                <strong>Использование диска:</strong> {bytesToMB(organization.storage_used)} MB из {bytesToMB(organization.storage_limit_bytes)} MB
                            </ListGroup.Item>
                        </ListGroup>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default AdminOrganizationDetails;
